import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatCurrency } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Calculator, TrendingDown, Clock } from "lucide-react";

/**
 * Side-by-side "what if" simulator for extra mortgage amortizations.
 * The user enters an amount and instantly sees the two possible outcomes
 * (reduce term vs reduce payment) fetched in parallel from the backend
 * simulate endpoint. Nothing is persisted — it's purely a preview.
 *
 * Default amount is 10% of the remaining balance (per user preference).
 */
export default function MortgageSimulatorDialog({ open, onOpenChange, debt }) {
  const currency = debt?.currency || "USD";
  const balance = Number(debt?.remaining_amount || 0);
  const defaultAmount = useMemo(
    () => (balance > 0 ? Math.round(balance * 0.1) : 0),
    [balance]
  );
  const [amount, setAmount] = useState(defaultAmount);
  const [rows, setRows] = useState({ reduce_term: null, reduce_payment: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset the amount whenever the dialog opens or the debt changes.
  useEffect(() => {
    if (open) {
      setAmount(defaultAmount);
      setRows({ reduce_term: null, reduce_payment: null });
      setError(null);
    }
  }, [open, defaultAmount]);

  useEffect(() => {
    if (!open || !debt?.id) return;
    const n = Number(amount);
    if (!(n > 0)) {
      setRows({ reduce_term: null, reduce_payment: null });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.post(`/debts/${debt.id}/extra-amortization-simulate`, {
        amount: n, mode: "reduce_term",
      }),
      api.post(`/debts/${debt.id}/extra-amortization-simulate`, {
        amount: n, mode: "reduce_payment",
      }),
    ])
      .then(([a, b]) => {
        if (cancelled) return;
        setRows({ reduce_term: a.data, reduce_payment: b.data });
      })
      .catch((e) => {
        if (cancelled) return;
        logger.warn("simulate parallel", e);
        setError(e?.response?.data?.detail || "No se pudo calcular la simulación");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, debt?.id, amount]);

  if (!debt) return null;

  const better = pickBetter(rows);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl bg-white" data-testid="simulator-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#1C7C54]" />
            Simulador de reamortización
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          {/* Amount input */}
          <div className="rounded-xl border border-[#EBEBEA] bg-[#FAFAF9] px-4 py-3">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] items-end gap-4">
              <div>
                <Label htmlFor="sim-amount" className="text-xs">Importe a amortizar ({currency})</Label>
                <Input
                  id="sim-amount"
                  data-testid="sim-amount-input"
                  type="number"
                  min="0"
                  step="100"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="text-xs text-[#8A9992] tabular-nums">
                Saldo actual · <b className="text-[#0A110E]">{formatCurrency(balance, currency)}</b>
              </div>
              <div className="flex gap-1">
                {[5, 10, 25].map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    data-testid={`sim-quick-${pct}`}
                    onClick={() => setAmount(Math.round(balance * pct / 100))}
                    className="px-2 py-1 text-[11px] rounded-md border border-[#EBEBEA] hover:border-[#1C7C54]/40 text-[#5F7368]"
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          {error && (
            <div className="text-sm text-[#D95D39] px-4" data-testid="sim-error">{error}</div>
          )}

          {loading && !rows.reduce_term && (
            <div className="text-sm text-[#8A9992] px-4" data-testid="sim-loading">Calculando…</div>
          )}

          {/* Side-by-side comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ModeCard
              testid="sim-card-reduce_term"
              icon={Clock}
              title="Reduce plazo"
              subtitle="Cuota igual · menos meses"
              data={rows.reduce_term}
              currency={currency}
              highlight={better === "reduce_term"}
              mode="reduce_term"
            />
            <ModeCard
              testid="sim-card-reduce_payment"
              icon={TrendingDown}
              title="Reduce cuota"
              subtitle="Meses igual · cuota más baja"
              data={rows.reduce_payment}
              currency={currency}
              highlight={better === "reduce_payment"}
              mode="reduce_payment"
            />
          </div>

          {(rows.reduce_term && rows.reduce_payment) && (
            <div
              data-testid="sim-verdict"
              className="text-xs text-[#5F7368] px-2"
            >
              {better === "reduce_term"
                ? "💡 Con este importe, reducir plazo ahorra más intereses a largo plazo."
                : better === "reduce_payment"
                ? "💡 Con este importe, reducir cuota libera más liquidez cada mes."
                : "Ambos modos son equivalentes con este importe."}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="sim-close"
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModeCard({ testid, icon: Icon, title, subtitle, data, currency, highlight, mode }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-4 flex flex-col gap-3 ${
        highlight
          ? "border-[#1C7C54] bg-[#1C7C54]/5"
          : "border-[#EBEBEA] bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${highlight ? "text-[#1C7C54]" : "text-[#5F7368]"}`} />
          <div>
            <div className="text-sm font-semibold text-[#0A110E]">{title}</div>
            <div className="text-[11px] text-[#8A9992]">{subtitle}</div>
          </div>
        </div>
        {highlight && (
          <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#1C7C54] bg-white rounded-md px-1.5 py-0.5 border border-[#1C7C54]/40">
            Mejor
          </span>
        )}
      </div>

      {!data ? (
        <div className="text-xs text-[#8A9992]">Introduce un importe para ver el impacto.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <SimStat
              label="Saldo tras amortizar"
              value={formatCurrency(data.balance_after, currency)}
            />
            <SimStat
              label={mode === "reduce_term" ? "Meses ahorrados" : "Nueva cuota"}
              value={mode === "reduce_term"
                ? `${data.months_saved} m.`
                : formatCurrency(data.monthly_after, currency)}
              tone="text-[#1C7C54]"
            />
            <SimStat
              label={mode === "reduce_term" ? "Cuota (sin cambio)" : "Cuota anterior"}
              value={formatCurrency(data.monthly_before, currency)}
              muted
            />
            <SimStat
              label={mode === "reduce_term" ? "Meses restantes" : "Meses (sin cambio)"}
              value={`${data.months_remaining_before} → ${data.months_remaining_after}`}
              muted
            />
          </div>
          {data.interest_saved_estimate > 0 && (
            <div className="text-xs text-[#5F7368] pt-2 border-t border-[#EBEBEA]">
              Ahorro estimado en intereses: <b className="text-[#1C7C54] tabular-nums">
                {formatCurrency(data.interest_saved_estimate, currency)}
              </b>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SimStat({ label, value, tone, muted }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">{label}</div>
      <div className={`font-display text-base font-semibold tabular-nums mt-0.5 ${
        muted ? "text-[#5F7368]" : (tone || "text-[#0A110E]")
      }`}>
        {value}
      </div>
    </div>
  );
}

// "Better" heuristic: whichever mode saves more interest wins. Ties = neither.
function pickBetter(rows) {
  const a = rows.reduce_term?.interest_saved_estimate;
  const b = rows.reduce_payment?.interest_saved_estimate;
  if (a === undefined || b === undefined) return null;
  if (Math.abs(a - b) < 1) return null;
  return a > b ? "reduce_term" : "reduce_payment";
}
